import { LitElement, html, css } from "lit";
import {unsafeHTML} from 'lit-html/directives/unsafe-html.js';
import { library, dom, icon, parse } from "@fortawesome/fontawesome-svg-core";
import { fas } from "@fortawesome/free-solid-svg-icons";
import { far } from "@fortawesome/free-regular-svg-icons";
import { fab } from "@fortawesome/free-brands-svg-icons";
import { fac } from "./custom-icons";

library.add(fas, far, fab, fac);

class MatterIcon extends LitElement {
  static get styles() {
    return css`
      :host {
        display: block;
        font-family: Source Sans Pro, sans-serif;

        --main-hover: #0b4c1a;
        --main: #087021;
        --main-help: #c4ddca;
        --main-specific-help: #f7fbf8;
        --main-backgroung: #ffffff;

        --grey-main: #333333;
        --grey-help: #bebebe;
        --grey-container: #eeeeee;
        --grey-specific-help: #f5f5f5;

        --status-success: #2abc67;
        --status-info: #5bc0de;
        --status-warning: #f0ad4e;
        --status-error: #d9534f;

        --graph-1: #77b5c3;
        --graph-2: #4863a3;
        --graph-3: #9371ac;
        --graph-4: #d87d7d;
        --graph-5: #d9a46c;
        --graph-6: #e3cd66;
      }

      svg {
        color: var(--icon-color, black);
        background-color: var(--icon-background-color, transparent);
      }

      /* THEMES  */
      :host([theme="primary"]) svg {
        --icon-color: #087021;
      }

      :host([theme="secondary"]) svg {
        --icon-color: white;
      }

      <<<<<<< HEAD ======= :host([theme="help"]) svg {
        --icon-color: #c4ddca;
      }

      >>>>>>>develop :host([theme="success"]) svg {
        --icon-color: #5c966a;
      }

      :host([theme="warning"]) svg {
        --icon-color: #f0ad4e;
      }

      :host([theme="info"]) svg {
        --icon-color: #5bc0de;
      }

      :host([theme="error"]) svg {
        --icon-color: #d9534f;
      }
    `;
  }

  static get properties() {
    return {
      // Icons
      family: { type: String },
      icon: { type: String },
      theme: { type: String, reflect: true },
      color: { type: String },

      // Classes
      className: { type: String },
      spin: { type: Boolean },
      pulse: { type: Boolean },
      fixedWidth: { type: Boolean },
      inverse: { type: Boolean },
      border: { type: Boolean },
      listItem: { type: Boolean },
      flip: { type: String },
      size: { type: String },
      rotation: { type: String },
      pull: { type: String },

      // Others
      alt: { type: String },
      mask: { type: String },
      transform: { type: String },
    };
  }

  constructor() {
    super();

    // Icons
    this.family = "fas";
    this.icon = "";
    this.theme = "";
    this.color = "";

    // Classes
    this.className = "";
    this.spin = false;
    this.pulse = false;
    this.fixedWidth = false;
    this.inverse = false;
    this.border = false;
    this.listItem = false;
    this.flip = null;
    this.size = null;
    this.rotation = null;
    this.pull = null;

    // Others
    this.alt = "";
    this.mask = "";
    this.transform = null;
  }

  get classes() {
    const {
      spin,
      pulse,
      fixedWidth,
      inverse,
      border,
      listItem,
      flip,
      size,
      rotation,
      pull,
    } = this;

    const classes = {
      "fa-spin": spin,
      "fa-pulse": pulse,
      "fa-fw": fixedWidth,
      "fa-inverse": inverse,
      "fa-border": border,
      "fa-li": listItem,
      "fa-flip-horizontal": flip === "horizontal" || flip === "both",
      "fa-flip-vertical": flip === "vertical" || flip === "both",
      [`fa-${size}`]: size !== null,
      [`fa-rotate-${rotation}`]: rotation !== null,
      [`fa-pull-${pull}`]: pull !== null,
    };

    return Object.keys(classes)
      .map((key) => (classes[key] ? key : null))
      .filter((key) => key);
  }

  /**
   * API de FontAwesome: https://fontawesome.com/how-to-use/with-the-api/methods/icon
   */
  fontawesome() {
    const {
      family: prefix,
      icon: iconName,
      className,
      alt: title,
      mask,
      transform,
      classes,
    } = this;

    const faIcon = icon(
      { prefix, iconName },
      {
        classes: [...classes, ...className.split(" ")],
        title,
        mask: mask === "" ? null : icon({ prefix, iconName: mask }),
        transform: parse.transform(transform),
      }
    );

    if (!faIcon) {
      // eslint-disable-next-line no-console
      console.error("MATTER-ICON: Icon not found", { prefix, iconName });
      return null;
    }
    return html(faIcon.html);
  }

  render() {

    var data = this.fontawesome();
    var dataString = data.strings[0];

    return html`
      <style>
        ${dom.css()}
      </style>
      ${this.css()} ${unsafeHTML(dataString)}
    `;
  }

  css() {
    return this.color !== ""
      ? html`<style>
          :host svg {
            color: ${this.color};
          }
        </style>`
      : "";
  }
}
if (!window.customElements.get("matter-icon")) {
  window.customElements.define("matter-icon", MatterIcon);
}
